/**
 * `/settings/users` — provisioning for Bosun.
 *
 * WHY THIS SCREEN EXISTS: the session bridge deliberately does not auto-provision (spec `C4`), so
 * a Frappe `User` must exist before a signed-in Clerk user can do anything. Without this screen
 * that means running `auth.create_user` in Postman for every joiner.
 *
 * THE MANAGER GATE BELOW IS NOT PROTECTION — and as of 2026-08-09 that is confirmed, not a
 * caveat. Two reasons, both worth knowing before trusting it:
 *   - Against live today the Vite proxy authenticates as `rfq@gmail.com`, which resolves to
 *     `purchase_manager`, so everyone passes. It only starts discriminating when `F1`/`F2` land.
 *   - **`auth.create_user` and `auth.update_user` are NOT restricted server-side.** Confirmed by
 *     the product owner, 2026-08-09 (ask `A7`). Any authenticated caller can create a
 *     `purchase_manager` or promote their own account by calling the method directly, and this
 *     gate changes nothing about that. It is latent only because every request currently arrives
 *     under one shared API token, so there is no "any authenticated caller" yet — the day the
 *     session bridge lands, it stops being latent.
 * It is UX — it keeps a screen nobody else needs out of the way. The server is what decides, and
 * right now the server decides yes. Do not add a feature here that relies on this gate holding.
 *
 * PENDING IS NOT "NOT A MANAGER". `useSession()`'s `isManager` falls back to a guess
 * (`SessionProvider`'s local stub role) until `auth.get_current_user` answers — and today that
 * query starts every render as pending, because `FrappeSessionGate` (which would pre-seed it
 * before the tree renders) only mounts when Clerk is configured AND the session bridge is on
 * (`VITE_BOSUN_SESSION_BRIDGE=0` is the documented state until `session_from_clerk` lands live;
 * the keyless mock path never mounts the gate at all — see `main.tsx`'s header). Redirecting on
 * that guess would bounce a real manager on every refresh, bookmark, or direct link: `Navigate
 * replace` unmounts this page immediately, and nothing re-navigates back when the answer flips
 * true a moment later. So the gate below waits for `useCurrentUser()` to settle — pending renders
 * the same loading idiom the page uses everywhere else — before deciding real vs. redirect.
 *
 * NOR IS A FAILED LOOKUP "NOT A MANAGER". `useCurrentUser()` is `retry: false`, so one transient
 * failure settles `isPending` to `false` with `data` left `undefined` — indistinguishable, if you
 * only check `isPending`, from a page that is ready to decide. Fold that into the redirect below
 * and a real manager gets silently bounced by a network blip, with the Users card on
 * `/settings` gone too (it's gated on the same `isManager`), so the whole feature looks deleted
 * rather than broken. `currentUser.isError` and a settled-but-null `data` (`parseCurrentUser`
 * found no usable email) therefore get their own state, with a retry affordance, same as pending.
 * Only `currentUser.isSuccess && data.role === 'purchase_manager'` counts as "genuinely resolved,
 * genuinely not a manager" — computed locally as `resolvedIsManager` rather than trusting
 * `useSession()`'s `isManager`, which still falls back to the local stub role whenever
 * `currentUser.data` is `undefined` or has a `null` role, for the same reason described above.
 */

import { Navigate, Link } from 'react-router-dom'
import { Button, Card, PageHead } from '../../../components/ui'
import { useSession } from '../../../components/SessionProvider'
import { useCurrentUser } from '../../../lib/api/useMasters'
import { useUsers } from '../../../lib/api/useUsers'
import { UserTable } from './UserTable'
import { AddUserForm } from './AddUserForm'
import { RestoreUserField } from './RestoreUserField'

export function UsersPage() {
  const { user } = useSession()
  const currentUser = useCurrentUser()

  // The only condition under which the redirect below does NOT fire: the lookup genuinely
  // succeeded AND the answer is genuinely a manager. Computed here rather than read off
  // `useSession().isManager` — see this file's header for why that value isn't trustworthy in
  // every state `currentUser` can be in.
  const resolvedIsManager = currentUser.isSuccess && currentUser.data?.role === 'purchase_manager'

  // Gates `auth.get_users` behind the same condition as the redirect, so a non-manager hitting
  // this URL directly never issues the request before being sent back to `/settings` (finding 7
  // of the 2026-08-09 whole-branch review). Not a security boundary — the server decides that —
  // it just makes the gate mean what it visually appears to mean.
  const users = useUsers(undefined, { enabled: resolvedIsManager })

  // Still resolving who's signed in: treat this as its own state rather than folding it into the
  // redirect below.
  if (currentUser.isPending) {
    return (
      <>
        <PageHead help="users"
          title="Users"
          subtitle="Who can sign in to Waves, and what they may do. Roles come from Frappe."
        />
        <Card>
          <div style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</div>
        </Card>
      </>
    )
  }

  // Failed, or succeeded with nothing usable — see this file's header. Neither is "resolved, not
  // a manager", so neither redirects; both offer a retry instead.
  if (currentUser.isError || currentUser.data === null) {
    return (
      <>
        <PageHead help="users"
          title="Users"
          subtitle="Who can sign in to Waves, and what they may do. Roles come from Frappe."
        />
        <Card>
          <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 8 }}>
            Could not confirm who you're signed in as, so this screen can't tell whether to show
            itself.{' '}
            {currentUser.error instanceof Error ? currentUser.error.message : 'Please try again.'}
          </div>
          <Button size="sm" onClick={() => void currentUser.refetch()}>
            Try again
          </Button>
        </Card>
      </>
    )
  }

  // Resolved, and not a manager — now the redirect means something.
  if (!resolvedIsManager) return <Navigate to="/settings" replace />

  return (
    <>
      <PageHead help="users"
        title="Users"
        subtitle="Who can sign in to Waves, and what they may do. Roles come from Frappe."
      />

      <div style={{ marginBottom: 12 }}>
        <Link to="/settings" className="linkish">
          ← Back to Settings
        </Link>
      </div>

      <Card style={{ marginBottom: 14 }}>
        <b>People</b>
        <div style={{ color: 'var(--muted)', fontSize: 12.5, margin: '6px 0 10px' }}>
          Everyone with a Waves user record. Someone signing in with Clerk but without a record
          here sees “your account isn’t set up in Waves yet”.
        </div>
        {users.isPending ? (
          <div style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</div>
        ) : users.isError ? (
          <div style={{ color: 'var(--danger)', fontSize: 13 }}>
            {users.error instanceof Error ? users.error.message : 'Could not load users.'}
          </div>
        ) : (
          <UserTable users={users.data ?? []} currentUserEmail={user} />
        )}
      </Card>

      <Card style={{ marginBottom: 14 }}>
        <b>Add a user</b>
        <div style={{ color: 'var(--muted)', fontSize: 12.5, margin: '6px 0 10px' }}>
          Creates the Waves record. They still sign in through Clerk with their own account.
        </div>
        <AddUserForm />
      </Card>

      <Card>
        <b>Restore a disabled user</b>
        <div style={{ marginTop: 6 }}>
          <RestoreUserField />
        </div>
      </Card>
    </>
  )
}
